import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { FiasWebSocket } from '../websocket';
import { authApi } from '../auth-api';
import { addBreadcrumb } from '../telemetry';

/**
 * Provider identifiers for Multi-AI streaming
 */
export type MultiAIProvider = 'openai' | 'anthropic' | 'google';

/**
 * Node ID mapping for each provider's streaming node
 * Uses generic IDs (stream-0, stream-1, stream-2) to allow flexible provider selection
 */
export const PROVIDER_NODE_IDS: Record<MultiAIProvider, string> = {
  openai: 'stream-0',
  anthropic: 'stream-1',
  google: 'stream-2',
};

/**
 * Streaming state for a single provider
 */
export interface ProviderStreamState {
  text: string;
  thinking: string;
  isComplete: boolean;
  error: { code: string; message: string } | null;
}

/**
 * Configuration options for the useMultiStreaming hook
 */
export interface UseMultiStreamingOptions {
  /** Request ID to subscribe to */
  requestId: string | null;
  /** JWT auth token for WebSocket connection */
  authToken: string | null;
  /** WebSocket endpoint URL */
  endpoint: string | null;
  /** Whether streaming is enabled (default: true) */
  enabled?: boolean;
  /** Callback when streaming text updates for a provider */
  onTextUpdate?: (provider: MultiAIProvider, text: string) => void;
  /** Callback when thinking text updates for a provider */
  onThinkingUpdate?: (provider: MultiAIProvider, text: string) => void;
  /** Callback when a provider completes */
  onProviderComplete?: (provider: MultiAIProvider) => void;
  /** Callback when all providers complete */
  onComplete?: (requestId: string) => void;
  /** Callback when an error occurs */
  onError?: (code: string, message: string) => void;
}

/**
 * Callback for one-off request streaming
 */
export interface OneOffStreamCallbacks {
  onToken?: (nodeId: string, text: string) => void;
  onComplete?: () => void;
  onError?: (message: string) => void;
}

/**
 * Result returned by the useMultiStreaming hook
 */
export interface UseMultiStreamingResult {
  /** Current connection state */
  connectionState: 'disconnected' | 'connecting' | 'connected' | 'reconnecting';
  /** Whether currently connected */
  isConnected: boolean;
  /** Whether subscribed to a request */
  isSubscribed: boolean;
  /** Streaming state for each provider */
  providers: Record<MultiAIProvider, ProviderStreamState>;
  /** Whether all providers have completed */
  isAllComplete: boolean;
  /** Last error (if any) */
  error: { code: string; message: string } | null;
  /** Manually connect to WebSocket */
  connect: () => Promise<void>;
  /** Manually disconnect from WebSocket */
  disconnect: () => void;
  /** Unsubscribe from current request without disconnecting */
  unsubscribe: () => void;
  /** Reset streaming state (clear text and completions) */
  reset: () => void;
  /** Subscribe to a one-off request with custom callbacks (for summary/assumptions) */
  subscribeOneOff: (requestId: string, callbacks: OneOffStreamCallbacks) => () => void;
}

/**
 * Get the provider from a node ID
 */
function getProviderFromNodeId(nodeId: string): MultiAIProvider | null {
  for (const [provider, expectedNodeId] of Object.entries(PROVIDER_NODE_IDS)) {
    if (nodeId === expectedNodeId) {
      return provider as MultiAIProvider;
    }
  }
  return null;
}

/**
 * Initial state for a provider
 */
const initialProviderState: ProviderStreamState = {
  text: '',
  thinking: '',
  isComplete: false,
  error: null,
};

/**
 * React hook for managing WebSocket streaming connections for Multi-AI
 *
 * Handles 3 parallel streams from OpenAI, Anthropic, and Google providers,
 * routing tokens to the correct provider based on nodeId.
 *
 * @example
 * ```tsx
 * function MultiAIChat({ requestId }: { requestId: string }) {
 *   const {
 *     connectionState,
 *     providers,
 *     isAllComplete,
 *     error,
 *   } = useMultiStreaming({
 *     requestId,
 *     authToken: 'your-jwt-token',
 *     endpoint: 'wss://api.example.com/stream',
 *   });
 *
 *   return (
 *     <div className="grid grid-cols-3 gap-4">
 *       {(['openai', 'anthropic', 'google'] as const).map(provider => (
 *         <div key={provider}>
 *           <h3>{provider}</h3>
 *           <p>{providers[provider].text}</p>
 *           {providers[provider].isComplete && <span>Complete</span>}
 *         </div>
 *       ))}
 *     </div>
 *   );
 * }
 * ```
 */
export function useMultiStreaming(options: UseMultiStreamingOptions): UseMultiStreamingResult {
  const {
    requestId,
    authToken,
    endpoint,
    enabled = true,
    onTextUpdate,
    onThinkingUpdate,
    onProviderComplete,
    onComplete,
    onError,
  } = options;

  // Connection state
  const [connectionState, setConnectionState] = useState<
    'disconnected' | 'connecting' | 'connected' | 'reconnecting'
  >('disconnected');
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [error, setError] = useState<{ code: string; message: string } | null>(null);

  // Provider streaming states
  const [openaiState, setOpenaiState] = useState<ProviderStreamState>({ ...initialProviderState });
  const [anthropicState, setAnthropicState] = useState<ProviderStreamState>({
    ...initialProviderState,
  });
  const [googleState, setGoogleState] = useState<ProviderStreamState>({ ...initialProviderState });
  const [isAllComplete, setIsAllComplete] = useState(false);

  // Refs
  const wsRef = useRef<FiasWebSocket | null>(null);
  const cleanupRef = useRef<(() => void)[]>([]);
  const currentRequestIdRef = useRef<string | null>(null);

  // Memoized providers object
  const providers = useMemo(
    () => ({
      openai: openaiState,
      anthropic: anthropicState,
      google: googleState,
    }),
    [openaiState, anthropicState, googleState],
  );

  // Reset state
  const reset = useCallback(() => {
    setOpenaiState({ ...initialProviderState });
    setAnthropicState({ ...initialProviderState });
    setGoogleState({ ...initialProviderState });
    setIsAllComplete(false);
    setError(null);
    setIsSubscribed(false);
  }, []);

  // Connect to WebSocket
  const connect = useCallback(async () => {
    addBreadcrumb(
      'multi-streaming',
      `connect called, hasAuthToken: ${!!authToken}, endpoint: ${endpoint}`,
    );
    if (!authToken || !endpoint) {
      addBreadcrumb('multi-streaming', 'Cannot connect: missing authToken or endpoint');
      return;
    }

    if (wsRef.current?.isConnected()) {
      addBreadcrumb('multi-streaming', 'Already connected');
      return;
    }

    // Create new WebSocket instance if needed
    if (!wsRef.current) {
      wsRef.current = new FiasWebSocket({ endpoint });
    }

    // Pass a ticket provider, not a static token: each (re)connect
    // mints a fresh single-use 30s ws-ticket so reconnects after a
    // network blip don't replay a consumed credential.
    addBreadcrumb('multi-streaming', 'Connecting with ticket provider');
    try {
      await wsRef.current.connect(() => authApi.getWebSocketTicket());
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to connect';
      const isAuthFailure = message === 'Authentication credential unavailable';
      setError({
        code: isAuthFailure ? 'UNAUTHORIZED' : 'CONNECTION_FAILED',
        message: isAuthFailure ? 'Session expired. Please log out and log back in.' : message,
      });
    }
  }, [authToken, endpoint]);

  // Disconnect from WebSocket
  const disconnect = useCallback(() => {
    wsRef.current?.disconnect();
    reset();
  }, [reset]);

  // Unsubscribe from current request (stale tokens are filtered by requestId)
  const unsubscribe = useCallback(() => {
    wsRef.current?.unsubscribe();
    currentRequestIdRef.current = null;
    reset();
  }, [reset]);

  // Set up WebSocket event handlers
  useEffect(() => {
    if (!enabled || !endpoint) {
      return;
    }

    // Create WebSocket instance
    if (!wsRef.current) {
      wsRef.current = new FiasWebSocket({ endpoint });
    }

    const ws = wsRef.current;

    // Set up event handlers
    const unsubConnectionChange = ws.onConnectionChange((state) => {
      addBreadcrumb('multi-streaming', `Connection state changed: ${state}`);
      setConnectionState(state);
    });

    const unsubToken = ws.onToken((nodeId, text, requestId) => {
      // Log all token details for debugging
      addBreadcrumb('multi-streaming', 'Token received', {
        nodeId,
        requestIdFromMessage: requestId,
        currentRequestId: currentRequestIdRef.current,
        match: requestId === currentRequestIdRef.current,
        textLength: text?.length,
      });
      console.log('[useMultiStreaming] Token received:', {
        nodeId,
        requestIdFromMessage: requestId,
        currentRequestId: currentRequestIdRef.current,
        match: requestId === currentRequestIdRef.current,
        textSnippet: text?.substring(0, 50),
      });

      // Ignore tokens from different requests (stale tokens after stop)
      if (requestId !== currentRequestIdRef.current) {
        addBreadcrumb('multi-streaming', 'Token REJECTED - requestId mismatch', {
          messageRequestId: requestId,
          expectedRequestId: currentRequestIdRef.current,
        });
        console.warn('[useMultiStreaming] Token REJECTED - requestId mismatch');
        return;
      }

      const provider = getProviderFromNodeId(nodeId);
      if (!provider) {
        addBreadcrumb('multi-streaming', `Received token for unknown nodeId: ${nodeId}`);
        return;
      }

      addBreadcrumb('multi-streaming', `Token ACCEPTED for provider: ${provider}`);

      const setterMap = {
        openai: setOpenaiState,
        anthropic: setAnthropicState,
        google: setGoogleState,
      };

      setterMap[provider]((prev) => ({
        ...prev,
        text: prev.text + text,
      }));
      onTextUpdate?.(provider, text);
    });

    const unsubThinking = ws.onThinking((nodeId, text, requestId) => {
      // Ignore thinking tokens from different requests (stale tokens after stop)
      if (requestId !== currentRequestIdRef.current) return;

      const provider = getProviderFromNodeId(nodeId);
      if (!provider) return;

      const setterMap = {
        openai: setOpenaiState,
        anthropic: setAnthropicState,
        google: setGoogleState,
      };

      setterMap[provider]((prev) => ({
        ...prev,
        thinking: prev.thinking + text,
      }));
      onThinkingUpdate?.(provider, text);
    });

    const unsubNodeComplete = ws.onNodeComplete((nodeId, requestId) => {
      // Ignore completion from different requests (stale after stop)
      if (requestId !== currentRequestIdRef.current) return;

      const provider = getProviderFromNodeId(nodeId);
      if (!provider) return;

      const setterMap = {
        openai: setOpenaiState,
        anthropic: setAnthropicState,
        google: setGoogleState,
      };

      setterMap[provider]((prev) => ({
        ...prev,
        isComplete: true,
      }));
      onProviderComplete?.(provider);
    });

    const unsubComplete = ws.onComplete((completedRequestId) => {
      addBreadcrumb(
        'multi-streaming',
        `Request complete: ${completedRequestId}, current: ${currentRequestIdRef.current}`,
      );
      // Only set isAllComplete if this completion is for our current request
      if (completedRequestId === currentRequestIdRef.current) {
        setIsAllComplete(true);
        onComplete?.(completedRequestId);
      }
    });

    const unsubError = ws.onError((code, message) => {
      addBreadcrumb('multi-streaming', `WebSocket error: ${code} - ${message}`);
      setError({ code, message });
      onError?.(code, message);
    });

    const unsubSubscribed = ws.onSubscribed(() => {
      addBreadcrumb('multi-streaming', 'Subscription confirmed by server');
      setIsSubscribed(true);
    });

    // Store cleanup functions
    cleanupRef.current = [
      unsubConnectionChange,
      unsubToken,
      unsubThinking,
      unsubNodeComplete,
      unsubComplete,
      unsubError,
      unsubSubscribed,
    ];

    // Cleanup on unmount
    return () => {
      cleanupRef.current.forEach((fn) => fn());
      cleanupRef.current = [];
    };
  }, [enabled, endpoint, onTextUpdate, onThinkingUpdate, onProviderComplete, onComplete, onError]);

  // Auto-connect when authToken and endpoint are available
  useEffect(() => {
    if (enabled && authToken && endpoint) {
      connect();
    }
  }, [enabled, authToken, endpoint, connect]);

  // Subscribe to requestId when it changes
  useEffect(() => {
    addBreadcrumb(
      'multi-streaming',
      `Subscribe effect: enabled=${enabled}, requestId=${requestId}, connectionState=${connectionState}`,
    );
    if (!enabled || !requestId || connectionState !== 'connected') {
      addBreadcrumb('multi-streaming', 'Not subscribing yet - waiting for connection');
      return;
    }

    // Reset state for new request
    addBreadcrumb('multi-streaming', `Subscribing to requestId: ${requestId}`);
    reset();

    // Track the current requestId so we can filter completion events
    currentRequestIdRef.current = requestId;

    // Subscribe to the new request
    wsRef.current?.subscribe(requestId);
    addBreadcrumb('multi-streaming', 'Subscribe message sent');
  }, [enabled, requestId, connectionState, reset]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      wsRef.current?.disconnect();
      wsRef.current = null;
    };
  }, []);

  // Subscribe to a one-off request with custom callbacks (for summary/assumptions)
  const subscribeOneOff = useCallback(
    (oneOffRequestId: string, callbacks: OneOffStreamCallbacks) => {
      if (!wsRef.current) {
        callbacks.onError?.('WebSocket not connected');
        return () => {};
      }

      const ws = wsRef.current;
      let isActive = true; // Track if this subscription is still active

      // Set up one-off event handlers that filter by requestId
      const unsubToken = ws.onToken((nodeId, text, requestId) => {
        // Only process tokens if this subscription is still active and matches our request
        if (isActive && requestId === oneOffRequestId) {
          callbacks.onToken?.(nodeId, text);
        }
      });

      const unsubComplete = ws.onComplete((completedRequestId) => {
        // Only trigger complete if it's for our specific request
        if (completedRequestId === oneOffRequestId) {
          callbacks.onComplete?.();
          cleanup();
        }
      });

      const unsubError = ws.onError((code, message) => {
        if (isActive) {
          callbacks.onError?.(`${code}: ${message}`);
          cleanup();
        }
      });

      const cleanup = () => {
        isActive = false;
        unsubToken();
        unsubComplete();
        unsubError();
      };

      // Subscribe to the request
      ws.subscribe(oneOffRequestId);

      return cleanup;
    },
    [],
  );

  return {
    connectionState,
    isConnected: connectionState === 'connected',
    isSubscribed,
    providers,
    isAllComplete,
    error,
    connect,
    disconnect,
    unsubscribe,
    reset,
    subscribeOneOff,
  };
}

export default useMultiStreaming;
